import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProtectedLink from "../components/ProtectedLink";
import { useAuth } from '../services/AuthContext';
import FeedbackSection from '../components/FeedbackSection';
import { ButtonPrimary, ButtonSecondary, LessonCard, Icon } from '../components/ComponentLibrary';
import { Layers, Cpu, Bot, Gamepad2, Code2 } from 'lucide-react';
import FeatureIcon from '../components/FeatureIcon';
import ScrollReveal from '../components/ScrollReveal';
import './HomeNew.css';

// Hero Visual Image (Static)
const HERO_IMAGE = process.env.PUBLIC_URL + "/img/hero_coding_dark_gradient.png";

// Helper Component for Animated Numbers
const StatCounter = ({ end, duration = 2000, suffix = "" }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        let startTime = null;
        let animationFrame;

        const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            // Use easeOutExo for smoother counting
            const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
            setCount(Math.floor(easeProgress * end));

            if (progress < 1) {
                animationFrame = requestAnimationFrame(animate);
            }
        };

        animationFrame = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animationFrame);
    }, [end, duration]);

    return <span>{count.toLocaleString()}{suffix}</span>;
};

export default function HomeNew() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [showVideoModal, setShowVideoModal] = useState(false);

    // Courses data
    const courses = [
        {
            id: 'lessons',
            title: 'الدروس التفاعلية',
            description: 'تعلم البرمجة خطوة بخطوة مع دروس ممتعة وتمارين عملية',
            icon: 'lessons',
            variant: 'ocean',
            progress: 0,
            path: '/lessons'
        },
        {
            id: 'quizzes',
            title: 'الاختبارات الذكية',
            description: 'اختبر معلوماتك واكتشف نقاط قوتك',
            icon: 'quizzes',
            variant: 'sky',
            progress: 0,
            path: '/quizzes'
        },
        {
            id: 'ai-tutor',
            title: 'المساعد الذكي',
            description: 'اسأل أي سؤال واحصل على إجابات فورية',
            icon: 'ai',
            variant: 'sunshine',
            progress: 0,
            path: '/ai-tutor'
        },
        {
            id: 'progress',
            title: 'تقدمي ومستواي',
            description: 'تتبع نقاطك، شاراتك، ومستواك',
            icon: 'progress',
            variant: 'success',
            progress: 0,
            path: '/progress'
        },
        {
            id: 'parent',
            title: 'لوحة الوالدين',
            description: 'متابعة تقدم أطفالك ونتائجهم',
            icon: 'parent',
            variant: 'ocean',
            progress: 0,
            path: '/parent'
        },
        {
            id: 'final-quiz',
            title: 'الاختبار النهائي',
            description: 'اختبر معلوماتك بتحدي شامل!',
            icon: 'finalQuiz',
            variant: 'sun',
            progress: 0,
            path: '/final-quiz'
        }
    ];

    return (
        <div className="home-container">
            <ScrollReveal direction="down" duration={0.8}>
                <section className="hero-section">
                    <div className="hero-content">
                        <div className="hero-welcome">
                            <Icon name="sparkles" size={24} className="hero-wave" color="var(--cw-sunshine-500)" />
                            <span className="hero-greeting">
                                مرحباً {user?.fullName || 'بك في عالم البرمجة'}!
                            </span>
                        </div>

                        <h1 className="hero-title">
                            ابدأ رحلتك في <span className="hero-highlight">عالم البرمجة</span>
                        </h1>

                        <p className="hero-subtitle">
                            تعلم البرمجة من الصفر مع أفضل المناهج التفاعلية والمشاريع العملية
                            <br />
                            انضم لآلاف الطلاب وابدأ رحلة التعلم الممتعة <Icon name="rocket" size={20} />
                        </p>

                        <div className="hero-actions">
                            <ButtonPrimary
                                size="lg"
                                onClick={() => navigate('/free-session')}
                                className="free-session-cta"
                            >
                                ✨ احجز سيشن مجانية الآن ✨
                            </ButtonPrimary>

                            <ButtonSecondary
                                size="lg"
                                onClick={() => navigate('/lessons')}
                            >
                                ابدأ التعلم مجاناً <Icon name="target" size={20} />
                            </ButtonSecondary>
                        </div>

                        {/* Stats Row */}
                        <div className="hero-stats">
                            <div className="hero-stat">
                                <div className="stat-number"><StatCounter end={10000} suffix="+" /></div>
                                <span className="stat-label">طالب نشط</span>
                            </div>
                            <div className="hero-stat">
                                <div className="stat-number"><StatCounter end={50} suffix="+" /></div>
                                <span className="stat-label">درس تفاعلي</span>
                            </div>
                            <div className="hero-stat">
                                <div className="stat-number"><StatCounter end={98} suffix="%" /></div>
                                <span className="stat-label">معدل الرضا</span>
                            </div>
                        </div>
                    </div>

                    <div className="hero-visual">
                        <div className="visual-image-wrapper">
                            <img
                                src={HERO_IMAGE}
                                alt="Code Wave Learning"
                                className="hero-static-img"
                                width="600"
                                height="500"
                                loading="eager"
                            />
                            <div className="hero-visual-gradient"></div>
                        </div>

                        <div className="code-snippet">
                            <span className="code-comment"># ابدأ رحلتك</span>
                            <br />
                            <span className="code-function">print</span>
                            <span className="code-paren">(</span>
                            <span className="code-string">"Hello, Code Wave!"</span>
                            <span className="code-paren">)</span>
                        </div>
                    </div>

                </section>
            </ScrollReveal>

            <ScrollReveal delay={0.2} duration={0.8}>
                <div className="ticker-section">
                    <div className="ticker-title">نتقن أحدث التقنيات</div>
                    <div className="ticker-wrap">
                        <div className="ticker-track">
                            {[...Array(2)].map((_, i) => (
                                <React.Fragment key={i}>
                                    <div className="ticker-item">
                                        <Layers size={22} className="ticker-icon-svg" />
                                        <span>Roblox</span>
                                    </div>
                                    <div className="ticker-item">
                                        <Cpu size={22} className="ticker-icon-svg" />
                                        <span>React</span>
                                    </div>
                                    <div className="ticker-item">
                                        <Bot size={22} className="ticker-icon-svg" />
                                        <span>AI Tools</span>
                                    </div>
                                    <div className="ticker-item">
                                        <Gamepad2 size={22} className="ticker-icon-svg" />
                                        <span>Scratch</span>
                                    </div>
                                    <div className="ticker-item">
                                        <Code2 size={22} className="ticker-icon-svg" />
                                        <span>Python</span>
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </ScrollReveal>

            <ScrollReveal direction="up" delay={0.4}>
                <section className="courses-section">
                    <div className="section-header">
                        <h2 className="section-title">استكشف منصتنا</h2>
                        <p className="section-subtitle">
                            كل ما تحتاجه لرحلة تعلم ممتعة وفعالة
                        </p>
                    </div>

                    <div className="cw-grid cw-grid-3">
                        {courses.map((course, index) => (
                            <ScrollReveal key={course.id} delay={0.1 * index} direction="up" distance={20}>
                                <ProtectedLink
                                    to={course.path}
                                    style={{ textDecoration: 'none' }}
                                >
                                    <LessonCard
                                        title={course.title}
                                        description={course.description}
                                        icon={course.icon}
                                        variant={course.variant}
                                        progress={course.progress}
                                    />
                                </ProtectedLink>
                            </ScrollReveal>
                        ))}
                    </div>
                </section>
            </ScrollReveal>

            <ScrollReveal direction="up" delay={0.2}>
                <section className="features-section">
                    <div className="section-header">
                        <FeatureIcon name="sparkles" variant="sunshine" size="md" className="mb-4" />
                        <h2 className="section-title">تجربة تعليمية بمواصفات عالمية</h2>
                        <p className="section-subtitle">
                            نحن لا نعلم البرمجة فقط، بل نصنع مبدعي المستقبل بأحدث التقنيات
                        </p>
                    </div>
                    <div className="features-grid">
                        <ScrollReveal direction="left" delay={0.1} distance={30} className="feature-card f-ai tier-primary">
                            <div className="feature-glow"></div>
                            <div className="feature-content">
                                <div className="feature-icon-wrapper">
                                    <FeatureIcon name="ai" variant="sunshine" size="lg" priority="hero" />
                                </div>
                                <div className="feature-title-group">
                                    <h3>المساعد الذكي (AI Assistant)</h3>
                                    <div className="feature-badge">متاح دائماً</div>
                                </div>
                                <p>مساعدك الشخصي المتوفر 24/7. اسأل عن أي كود، واطلب شرح المفاهيم المعقدة، واحصل على حلول ذكية فورية لكل مشاكلك البرمجية.</p>
                            </div>
                        </ScrollReveal>

                        <ScrollReveal direction="up" delay={0.2} distance={30} className="feature-card f-progress tier-secondary">
                            <div className="feature-icon-wrapper">
                                <FeatureIcon name="progress" variant="success" size="md" priority="primary" />
                            </div>
                            <h3>تتبع التقدم والمهارات</h3>
                            <p>نظام ذكي يحلل نقاط قوتك ويطور مهاراتك خطوة بخطوة من خلال داشبورد تفاعلية توضح مدى تطورك.</p>
                        </ScrollReveal>

                        <ScrollReveal direction="right" delay={0.3} distance={30} className="feature-card f-cert tier-secondary">
                            <div className="feature-icon-wrapper">
                                <FeatureIcon name="finalQuiz" variant="sun" size="md" priority="primary" />
                            </div>
                            <h3>شهادات احترافية</h3>
                            <p>توثق إنجازاتك بشهادات معتمدة بعد كل مسار، لتكون بداية قوية في ملفك الشخصي كبرمج شاب.</p>
                        </ScrollReveal>

                        <ScrollReveal direction="up" delay={0.4} distance={30} className="feature-card f-updates tier-supporting">
                            <div className="feature-icon-wrapper">
                                <FeatureIcon name="sparkles" variant="sky" size="sm" priority="supporting" />
                            </div>
                            <h3>تحديثات مستمرة</h3>
                            <p>محتوى متجدد أسبوعياً يواكب أحدث لغات البرمجة (Web, Scratch, Python) وأدوات الذكاء الاصطناعي.</p>
                        </ScrollReveal>

                        <ScrollReveal direction="up" delay={0.5} distance={30} className="feature-card f-community tier-supporting">
                            <div className="feature-icon-wrapper">
                                <FeatureIcon name="parent" variant="ocean" size="sm" priority="supporting" />
                            </div>
                            <h3>مجتمع المبدعين</h3>
                            <p>بيئة تعليمية محفزة تجمعك مع آلاف الطلاب لتبادل المشاريع والتنافس في المسابقات البرمجية الدورية.</p>
                        </ScrollReveal>

                        <ScrollReveal direction="up" delay={0.6} distance={30} className="feature-card f-lessons tier-supporting">
                            <div className="feature-icon-wrapper">
                                <FeatureIcon name="lessons" variant="ocean" size="sm" priority="supporting" />
                            </div>
                            <h3>تعلم بالممارسة والتطبيق</h3>
                            <p>نطبق منهجية (Learning by Doing) حيث لا نكتفي بالشرح بل نطبق فوراً بمشاريع حقيقية.</p>
                        </ScrollReveal>
                    </div>
                </section>
            </ScrollReveal>

            {/* Customer Feedback */}
            <FeedbackSection />

            {/* Video Modal */}
            {showVideoModal && (
                <div className="video-modal-overlay" onClick={() => setShowVideoModal(false)}>
                    <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="close-video-btn"
                            onClick={() => setShowVideoModal(false)}
                        >
                            ✕
                        </button>
                        <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
                            <p style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                color: 'white',
                                fontSize: '1.5rem',
                                textAlign: 'center'
                            }}>
                                <Icon name="play" size={48} className="mb-4" />
                                <br />
                                سيتم إضافة فيديو تعريفي قريباً
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
